import React, { useState, useRef, useEffect } from 'react';

const Services = () => {
  const [expandedItems, setExpandedItems] = useState({});
  const itemRefs = useRef([]);
  const sectionRef = useRef(null);

  const services = [
    { 
      id: 1, 
      title: "End-to-end Assistance", 
      subtitle: "From listing to handover", 
      description: "Comprehensive support encompassing valuation, strategic marketing, expert negotiation, and seamless closing processes.",
      details: ["Property valuation", "Strategic marketing", "Expert negotiation", "Seamless closing"],
      accent: "#f3b33d"
    },
    { 
      id: 2, 
      title: "Real Estate Advising", 
      subtitle: "High-value deal expertise", 
      description: "Strategic counsel focused on maximizing investor ROI while implementing robust legal safeguards.",
      details: ["Investment strategy", "ROI optimization", "Legal compliance", "Risk assessment"],
      accent: "#d4af37"
    },
    { 
      id: 3, 
      title: "Legal/Transaction Advising", 
      subtitle: "Secure and compliant deals", 
      description: "Meticulous oversight ensuring all transactions adhere to regulatory requirements and industry standards.",
      details: ["Contract review", "Regulatory verification", "Title search", "Documentation"],
      accent: "#c9b037"
    },
    { 
      id: 4, 
      title: "Client Relationship Management", 
      subtitle: "International & local buyers", 
      description: "Personalized service management for discerning clientele spanning Dubai, Poland, and global markets.",
      details: ["Client onboarding", "Market navigation", "Cultural support", "24/7 Communication"],
      accent: "#b8a037"
    },
    { 
      id: 5, 
      title: "Digital Marketing & Leads", 
      subtitle: "Quality buyer funnels", 
      description: "Sophisticated campaign implementation driving premium visibility and generating qualified investor leads.",
      details: ["Premium campaigns", "Lead generation", "Social strategy", "Performance tracking"],
      accent: "#a89237"
    },
    { 
      id: 6, 
      title: "International Support", 
      subtitle: "Dubai, Poland markets", 
      description: "Expert navigation of complex international transactions with cultural and regulatory precision.",
      details: ["Cross-border expertise", "Legal compliance", "Market adaptation", "Network coordination"],
      accent: "#988437"
    }
  ];

  /* ===== Section Entrance Effect ===== */
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('reveal-visible');
        }
      },
      { threshold: 0.15 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  /* ===== Existing Card Stagger (UNCHANGED) ===== */
  useEffect(() => {
    itemRefs.current.forEach((item, index) => {
      if (item) {
        setTimeout(() => {
          item.style.opacity = '1';
          item.style.transform = 'translateY(0)';
        }, index * 100);
      }
    });
  }, []);

  const toggleExpand = (serviceId) => {
    setExpandedItems(prev => ({
      ...prev,
      [serviceId]: !prev[serviceId]
    }));
  };

  const setItemRef = (el, index) => {
    itemRefs.current[index] = el;
  };

  return (
    <>
      <style>
        {`
          @import url('https://fonts.googleapis.com/css2?family=Comfortaa:wght@300;400;700&family=Cormorant+Garamond:wght@400;500;600&display=swap');

          /* ===== Section Entrance Animation ===== */
          .reveal {
            opacity: 0;
            transform: translateY(40px);
            transition: opacity 0.9s ease, transform 0.9s ease;
          }

          .reveal-visible {
            opacity: 1;
            transform: translateY(0);
          }

          .services-section {
            background: linear-gradient(135deg, #0a0f1c 0%, #0c1120 100%);
            min-height: 100vh;
            padding: 5rem 1.5rem;
            color: #ffffff;
            font-family: 'Comfortaa', cursive;
          }

          .container {
            max-width: 650px;
            margin: 0 auto;
          }

          .header {
            text-align: left;
            margin-bottom: 4rem;
            border-left: 3px solid #f3b33d;
            padding-left: 1.5rem;
          }

          .pre-title {
            color: #f3b33d;
            font-size: 0.9rem;
            letter-spacing: 0.3em;
            text-transform: uppercase;
            margin-bottom: 0.6rem;
            display: block;
          }

          .header h2 {
            font-family: 'Cormorant Garamond', serif;
            font-size: 3rem;
            margin: 0;
            font-weight: 500;
          }

          .service-list {
            display: flex;
            flex-direction: column;
            gap: 1.2rem;
          }

          .service-card {
            background: rgba(255, 255, 255, 0.02);
            border: 1px solid rgba(255, 255, 255, 0.05);
            border-radius: 12px;
            padding: 1.8rem;
            transition: all 0.4s ease;
            opacity: 0;
            transform: translateY(20px);
            cursor: pointer;
          }

          .service-card.expanded {
            border-color: rgba(243, 179, 61, 0.4);
            background: rgba(255, 255, 255, 0.04);
          }

          .card-main {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
          }

          .card-content {
            flex: 1;
          }

          .service-subtitle {
            color: var(--accent);
            font-size: 0.8rem;
            text-transform: uppercase;
            letter-spacing: 0.15em;
            font-weight: 700;
            margin-bottom: 0.5rem;
          }

          .card-content h3 {
            font-family: 'Cormorant Garamond', serif;
            font-size: 1.85rem;
            margin: 0 0 1rem 0;
            font-weight: 500;
          }

          .description {
            color: #9aa7b2;
            font-size: 1.05rem;
            line-height: 1.6;
            margin: 0;
            max-width: 95%;
          }

          .toggle-btn {
            width: 44px;
            height: 44px;
            position: relative;
            display: flex;
            align-items: center;
            justify-content: center;
            margin-top: 5px;
          }

          .btn-line {
            position: absolute;
            background: #f3b33d;
            transition: all 0.3s cubic-bezier(0.65, 0.05, 0.36, 1);
          }

          .line-1 { width: 20px; height: 2px; }
          .line-2 { width: 2px; height: 20px; }

          .expanded .line-2 {
            transform: rotate(90deg);
            background: #ffffff;
          }

          .details-box {
            max-height: 0;
            overflow: hidden;
            transition: max-height 0.5s ease;
          }

          .expanded .details-box {
            max-height: 400px;
            margin-top: 2rem;
          }

          .grid {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 1.2rem;
            border-top: 1px solid rgba(255, 255, 255, 0.05);
            padding-top: 1.5rem;
          }

          .point {
            font-size: 0.95rem;
            color: #ffffff;
            opacity: 0;
            transform: translateX(-10px);
            display: flex;
            align-items: center;
            gap: 10px;
          }

          .expanded .point {
            animation: slideIn 0.4s forwards;
          }

          .expanded .point:nth-child(1) { animation-delay: 0.1s; }
          .expanded .point:nth-child(2) { animation-delay: 0.2s; }
          .expanded .point:nth-child(3) { animation-delay: 0.3s; }
          .expanded .point:nth-child(4) { animation-delay: 0.4s; }

          @keyframes slideIn {
            to { opacity: 1; transform: translateX(0); }
          }

          .dot {
            width: 5px;
            height: 5px;
            background: #f3b33d;
            border-radius: 50%;
          }

          @media (max-width: 480px) {
            .header h2 { font-size: 2.5rem; }
            .card-content h3 { font-size: 1.6rem; }
            .description { font-size: 1rem; }
            .grid { grid-template-columns: 1fr; }
          }
        `}
      </style>

      <section ref={sectionRef} className="services-section reveal">
        <div className="container">
          <div className="header">
            <span className="pre-title">Exclusive Expertise</span>
            <h2>My Services</h2>
          </div>

          <div className="service-list">
            {services.map((service, index) => (
              <div
                key={service.id}
                ref={(el) => setItemRef(el, index)}
                className={`service-card ${expandedItems[service.id] ? 'expanded' : ''}`}
                style={{ '--accent': service.accent }}
                onClick={() => toggleExpand(service.id)}
              >
                <div className="card-main">
                  <div className="card-content">
                    <div className="service-subtitle">{service.subtitle}</div>
                    <h3>{service.title}</h3>
                    <p className="description">{service.description}</p>
                  </div>

                  <div className="toggle-btn">
                    <div className="btn-line line-1"></div>
                    <div className="btn-line line-2"></div>
                  </div>
                </div>

                <div className="details-box">
                  <div className="grid">
                    {service.details.map((detail, idx) => (
                      <div key={idx} className="point">
                        <div className="dot"></div>
                        {detail}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default Services;
